import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import {
  addSymbol,
  addSymbolArray,
  deleteSymbol,
  deleteSymbols,
  toggleSymbol
} from '@/slices/symbolSelectorSlice';
import { SelectedSymbol } from '@/types/symbols';

const useSelectionFunctions = () => {
  const dispatch = useAppDispatch();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const isForwardMode = useAppSelector(state => state.symbolSelector.isForwardMode);

  const toggleSymbolInSelection = useCallback(
    (symbol: SelectedSymbol): void => {
      dispatch(toggleSymbol({ symbol }));
    },
    [dispatch]
  );

  // Create a fast lookup Set for selected symbols to avoid O(n) scans in every cell.
  const selectedKeySet = useMemo(() => {
    const toKey = (s: SelectedSymbol) =>
      isForwardMode
        ? `${s.Code}|${s.PeriodType}|${s.TimeRef}`
        : `${s.Code}|${s.Period}|${s.PeriodType}|${s.TimeRef}`;
    const set = new Set<string>();
    for (const s of symbols) set.add(toKey(s));
    return { set, toKey } as const;
  }, [symbols, isForwardMode]);

  const isSymbolSelected = useCallback(
    (selectedSymbol: SelectedSymbol | undefined) => {
      if (!selectedSymbol) return false;
      return selectedKeySet.set.has(selectedKeySet.toKey(selectedSymbol));
    },
    [selectedKeySet]
  );

  const allSymbolsSelected = useCallback(
    (arr: SelectedSymbol[]) => {
      return arr.every(symbol => selectedKeySet.set.has(selectedKeySet.toKey(symbol)));
    },
    [selectedKeySet]
  );

  const addSymbolToSelection = (symbol: SelectedSymbol) => {
    dispatch(addSymbol({ symbol }));
  };

  const addAllSymbolsToSelection = (symbols: SelectedSymbol[]) => {
    dispatch(addSymbolArray({ symbols }));
  };

  const removeSymbolsFromSelection = (symbols: Array<SelectedSymbol>) => {
    dispatch(deleteSymbols({ symbols }));
  };

  const removeSymbolFromSelection = (symbol: SelectedSymbol) => {
    if (symbol.Code.length) {
      dispatch(deleteSymbol({ symbol }));
    }
  };

  const addSymbolsArrayToSelection = (symbols: SelectedSymbol[]) => {
    dispatch(addSymbolArray({ symbols }));
  };

  const noneSelected = symbols.length < 1;

  return {
    toggleSymbolInSelection,
    addAllSymbolsToSelection,
    allSymbolsSelected,
    removeSymbolFromSelection,
    removeSymbolsFromSelection,
    isSymbolSelected,
    addSymbolsArrayToSelection,
    addSymbolToSelection,
    noneSelected
  };
};

export default useSelectionFunctions;
